'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@brew/ui/dropdown-menu'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@brew/ui/sheet'
import { useIsMobile } from '@brew/ui/use-is-mobile'
import {
  Image as ImageIcon,
  Link as LinkIcon,
  PaperclipIcon,
  Plus,
  XIcon,
} from 'lucide-react'
import Image from 'next/image'
import {
  type ComponentProps,
  Fragment,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useState,
} from 'react'
import { AttachmentPreviewDialog } from '@/components/ai-elements/prompt-input/attachment-preview-dialog'
import { ImageFromLinkDialog } from '@/components/ui/image-from-link-dialog'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import { appToast } from '@/lib/shared/toast'
import { importUserImageUrl } from '@/lib/uploads/upload-user-image'
import type { PromptInputAttachment as PromptInputAttachmentData } from './attachments'
import { PromptInputButton } from './primitives'
import { usePromptInputAttachments } from './provider'

export type PromptInputAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: PromptInputAttachmentData
  className?: string
}

export function PromptInputAttachment({
  data,
  className,
  ...props
}: PromptInputAttachmentProps) {
  const attachments = usePromptInputAttachments()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const filename = data.filename || ''
  const mediaType =
    data.mediaType?.startsWith('image/') && data.url ? 'image' : 'file'
  const isImage = mediaType === 'image'
  const attachmentLabel = filename || (isImage ? 'Image' : 'Attachment')

  const handleOpenPreview = useCallback(() => {
    setIsPreviewOpen(true)
  }, [])

  const handleRemoveAttachment = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      attachments.remove(data.id)
    },
    [attachments, data.id]
  )

  return (
    <>
      <div
        className={cn(
          'group relative flex h-fit cursor-pointer select-none items-center gap-1.5 rounded-lg border border-border p-1 font-medium text-xs transition-all hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
          className
        )}
        key={data.id}
        onClick={handleOpenPreview}
        {...props}
      >
        <div className="relative size-5 shrink-0">
          <div className="absolute inset-0 flex size-5 items-center justify-center overflow-hidden rounded bg-background transition-opacity group-hover:opacity-0">
            {isImage ? (
              <Image
                alt={filename || 'attachment'}
                className="size-5 object-cover"
                src={data.url}
                width={20}
                height={20}
                sizes="20px"
                quality={60}
                unoptimized
              />
            ) : (
              <div className="flex size-5 items-center justify-center text-muted-foreground">
                <PaperclipIcon className="size-3" />
              </div>
            )}
          </div>
          <Button
            aria-label="Remove attachment"
            className="!p-0 absolute inset-0 size-5 cursor-pointer rounded opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100"
            onClick={handleRemoveAttachment}
            type="button"
            variant="ghost"
          >
            <XIcon className="size-3.5" />
            <span className="sr-only">Remove</span>
          </Button>
        </div>

        <span className="flex-1 truncate">{attachmentLabel}</span>
      </div>

      <AttachmentPreviewDialog
        open={isPreviewOpen}
        filename={filename}
        mediaType={data.mediaType}
        url={data.url}
        isImage={isImage}
        onOpenChange={setIsPreviewOpen}
      />
    </>
  )
}

export type PromptInputAttachmentsProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> & {
  children: (attachment: PromptInputAttachmentData) => ReactNode
}

export function PromptInputAttachments({
  children,
}: PromptInputAttachmentsProps) {
  const attachments = usePromptInputAttachments()

  if (!attachments.files.length) {
    return null
  }

  return attachments.files.map((file) => (
    <Fragment key={file.id}>{children(file)}</Fragment>
  ))
}

export type PromptInputActionAddAttachmentsProps = ComponentProps<
  typeof DropdownMenuItem
> & {
  label?: string
}

export const PromptInputActionAddAttachments = ({
  label = 'Add photos or files',
  ...props
}: PromptInputActionAddAttachmentsProps) => {
  const attachments = usePromptInputAttachments()

  return (
    <DropdownMenuItem
      {...props}
      onSelect={(event) => {
        event.preventDefault()
        attachments.openFileDialog()
      }}
    >
      <ImageIcon className="mr-2 size-4" /> {label}
    </DropdownMenuItem>
  )
}

type PromptInputActionMenuMobileProps = {
  children?: ReactNode
  allowImageUrl?: boolean
}

function ImageUrlAttachmentDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const attachments = usePromptInputAttachments()
  const [isImporting, setIsImporting] = useState(false)

  const handleSubmit = useCallback(
    async (url: string) => {
      if (isImporting) {
        return
      }

      setIsImporting(true)
      try {
        const image = await importUserImageUrl(url)
        attachments.add([
          {
            url: image.url,
            mediaType: image.mediaType,
            filename: image.filename,
          },
        ])
        onOpenChange(false)
      } catch (error) {
        appToast.error(getErrorMessage(error, 'Could not import image URL'))
      } finally {
        setIsImporting(false)
      }
    },
    [attachments, isImporting, onOpenChange]
  )

  return (
    <ImageFromLinkDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Attach image from link"
      description="Brew fetches, optimizes, and converts the image before attaching it to this chat."
      submitLabel="Attach image"
      pendingLabel="Importing..."
      submitIcon={<LinkIcon className="size-4" />}
      isPending={isImporting}
      onSubmit={handleSubmit}
    />
  )
}

export const PromptInputActionMenuMobile = ({
  children: _children,
  allowImageUrl = true,
}: PromptInputActionMenuMobileProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isUrlDialogOpen, setIsUrlDialogOpen] = useState(false)
  const isMobile = useIsMobile()
  const attachments = usePromptInputAttachments()

  const handleAddFiles = useCallback(() => {
    attachments.openFileDialog()
    setIsDrawerOpen(false)
  }, [attachments])

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true)
  }

  const handleAddImageUrl = useCallback(() => {
    setIsDrawerOpen(false)
    setIsUrlDialogOpen(true)
  }, [])

  const handleUrlDialogChange = useCallback((open: boolean) => {
    setIsUrlDialogOpen(open)
  }, [])

  if (isMobile) {
    return (
      <>
        <PromptInputButton
          type="button"
          onClick={handleOpenDrawer}
          aria-label="Add attachment"
        >
          <Plus className="size-4" />
        </PromptInputButton>

        <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <SheetContent
            side="bottom"
            aria-describedby={undefined}
            overlayClassName="z-40 backdrop-blur-xs"
            className="bottom-3 z-40 mx-auto w-[95%] rounded-lg border border-border p-4"
          >
            <SheetHeader className="flex-row items-center justify-between p-0 text-muted-foreground">
              <SheetTitle className="font-medium text-muted-foreground text-sm">
                Add attachment
              </SheetTitle>
              <SheetClose
                aria-label="Close"
                className="cursor-pointer rounded-lg p-1"
              >
                <XIcon className="size-5" />
              </SheetClose>
            </SheetHeader>
            <ul className="flex flex-col overflow-hidden rounded-lg border border-border text-sm">
              <li className="border-border border-b last:border-b-0">
                <button
                  type="button"
                  onClick={handleAddFiles}
                  className="flex w-full cursor-pointer items-center gap-3 p-3 text-left transition-colors hover:bg-accent/50"
                >
                  <ImageIcon className="size-4 text-foreground" />
                  <span className="font-medium text-foreground">
                    Add photos or files
                  </span>
                </button>
              </li>
              {allowImageUrl ? (
                <li className="border-border border-b last:border-b-0">
                  <button
                    type="button"
                    onClick={handleAddImageUrl}
                    className="flex w-full cursor-pointer items-center gap-3 p-3 text-left transition-colors hover:bg-accent/50"
                  >
                    <LinkIcon className="size-4 text-foreground" />
                    <span className="font-medium text-foreground">
                      Add image from link
                    </span>
                  </button>
                </li>
              ) : null}
            </ul>
          </SheetContent>
        </Sheet>
        {allowImageUrl ? (
          <ImageUrlAttachmentDialog
            open={isUrlDialogOpen}
            onOpenChange={handleUrlDialogChange}
          />
        ) : null}
      </>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <PromptInputButton type="button" aria-label="Add attachment">
            <Plus className="size-4" />
          </PromptInputButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={handleAddFiles}>
            <ImageIcon className="size-4" />
            Add photos or files
          </DropdownMenuItem>
          {allowImageUrl ? (
            <DropdownMenuItem onSelect={handleAddImageUrl}>
              <LinkIcon className="size-4" />
              Add image from link
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {allowImageUrl ? (
        <ImageUrlAttachmentDialog
          open={isUrlDialogOpen}
          onOpenChange={handleUrlDialogChange}
        />
      ) : null}
    </>
  )
}
